import { ExecutionPath } from '../../models/index';
import { ModelingUtil } from '@ispm/shared/bpmn-io/modeling-util';
import { HEAD_NODE_ID } from '@ispm-shared/modules/bpmn';
import { Store } from '@ngrx/store';
import { State } from '../../store/state';
import { isEmpty } from 'lodash';
import { Shape } from 'diagram-js/lib/model';
import { TreeNavigator } from '@ispm/shared/bpmn-io/services/tree-navigator';
import {
  selectMandatoryNodesVisited,
  selectNodesDependencies,
  selectNodeSelectionMode
} from '@ispm-client/modules/process-executor/store/selectors';
import { NodeSelectionMode } from '@ispm-client/modules/process-executor/models';
import * as _ from 'lodash';
import {NodeDependencies} from "@ispm/shared/bpmn/models/node-dependencies";


export enum CssClassesNames {
  DIM_CSS_CLASS = 'dim',
  CLICKABLE_CSS_CLASS = 'clickable'
}

export class DimNodesService {
  private nodesData: NodeDependencies;
  private mandatoryNotesVisited: string[];
  private nodeSelectionMode: NodeSelectionMode;

  constructor(private store$: Store<State>,
              protected treeNavigator: TreeNavigator,
              private elementRegistry) {
    this.store$.select(selectNodesDependencies).subscribe(data =>
      this.nodesData = data
    );
    this.store$.select(selectMandatoryNodesVisited).subscribe(nodes =>
      this.mandatoryNotesVisited = nodes
    );
    this.store$.select(selectNodeSelectionMode).subscribe(mode =>
      this.nodeSelectionMode = mode
    );
  }

  dimNodes(executionPath: ExecutionPath) {
    if (!_.isEmpty(this.nodesData) && this.nodeSelectionMode === NodeSelectionMode.anyNode) {
      const currentNeighborhoodHeadNodeId = this.treeNavigator.getCurrentNeighborhoodHeadNodeId();

      let rootElement;
      if (currentNeighborhoodHeadNodeId == null) {
        rootElement = this.elementRegistry.get(HEAD_NODE_ID);
      } else {
        rootElement = this.elementRegistry.get(currentNeighborhoodHeadNodeId);
      }
      const nodesAtCurrentLevel = ModelingUtil.getDescendentNodesAtCurrentLayer(rootElement);
      let executedNodesIds: string[] = this.getExecutedNodesIds(executionPath);
      let mandatoryNotesVisited = this.mandatoryNotesVisited || [];
      nodesAtCurrentLevel.forEach((node) => {
        // filter by node.id to get its data object
        let dependenciesInExecutedPath: string[] = [];
        const nodeDependencies: string[] = Array.from(this.nodesData.dependencies.get(node.id));
        let effectiveDependencies: string[] = [];
        /* Multiple dependencies mean multiple routes to the node; replace mandatory note dependencies that were
         * already visited with their replacements.
         */
        if (nodeDependencies.length > 1 && !_.isEmpty(mandatoryNotesVisited)) {
          for (const dep of nodeDependencies) {
            if (mandatoryNotesVisited.includes(dep, 0) && this.nodesData.replacements.has(dep)) {
              effectiveDependencies.push(... Array.from(this.nodesData.replacements.get(dep)));
            } else {
              effectiveDependencies.push(dep);
            }
          }
        } else {
          // A single dependency means a mandatory note; if the mandatory note has been displayed, it is no longer a dependency
          effectiveDependencies = (nodeDependencies.length === 1 && mandatoryNotesVisited.includes(nodeDependencies[0], 0)) ? [] : nodeDependencies;
        }
        if (!isEmpty(effectiveDependencies) && !isEmpty(executedNodesIds)) {
          dependenciesInExecutedPath = effectiveDependencies.filter(id => executedNodesIds.includes(id, 0));
        }

        if (!isEmpty(executedNodesIds) && !executedNodesIds.includes(node.id, 0)) {

          if (isEmpty(dependenciesInExecutedPath) && !isEmpty(effectiveDependencies)) { // in case the executionPath is not empty and no one of the dependencies of the node is in the executionPath
            // dim node
            this.dimNode(node.id);
          } else {
            // unDim node
            this.UnDimNode(node.id);
          }

        } else if (isEmpty(executedNodesIds) && !isEmpty(effectiveDependencies)) { // in case the executionPath is empty and the node has dependencies
          // dim node
          this.dimNode(node.id);
        } else {
          // unDim node
          this.UnDimNode(node.id);
        }
      });
    }
  }

  private dimNode(nodeId: string) {
    const node: Shape = this.getShapeByID(nodeId);
    const gfx = this.elementRegistry.getGraphics(node);
    let nodeCssClass: String = gfx.getAttribute('class');
    if (nodeCssClass.indexOf(CssClassesNames.DIM_CSS_CLASS) === -1) {
      if (!isEmpty(nodeCssClass)) {
        nodeCssClass += ' ';
      }
      nodeCssClass += CssClassesNames.DIM_CSS_CLASS;
      gfx.setAttribute('class', nodeCssClass);
    }
  }

  private UnDimNode(nodeId: any) {
    const node: Shape = this.getShapeByID(nodeId);
    const gfx = this.elementRegistry.getGraphics(node);
    let nodeCssClass: String = gfx.getAttribute('class');
    if (nodeCssClass.indexOf(CssClassesNames.DIM_CSS_CLASS) !== -1) {
      nodeCssClass = nodeCssClass.replace(CssClassesNames.DIM_CSS_CLASS, '');
      gfx.setAttribute('class', nodeCssClass);
    }
    gfx.setAttribute('class', nodeCssClass + ' ' + CssClassesNames.CLICKABLE_CSS_CLASS);
  }

  private getShapeByID(nodeId: string): Shape {
    return (nodeId) ? this.elementRegistry.get(nodeId) : null;
  }

  /*
  * Get all Executed node Ids:
  *   executionPath:
  *     a. executedNodes
  *     b. currentUserTaskHolder
  *     c. executedTreatmentTaskId
 */
  private getExecutedNodesIds(executionPath: ExecutionPath): string[] {
    const executedNodesIds =
      executionPath.executedNodes ? executionPath.executedNodes.map(node => node.nodeId) : [] ;
    if (executionPath.currentUserTaskHolder) {
      executedNodesIds.push(executionPath.currentUserTaskHolder.nodeId);
    }
    executedNodesIds.push(executionPath.executedTreatmentTaskId);
    return executedNodesIds.filter(id => id != null);
  }

}
